export enum GoogleAdsCampaignType {
  SEARCH = 'SEARCH',
  DISPLAY = 'DISPLAY',
  VIDEO = 'VIDEO', // YouTube ads
  SHOPPING = 'SHOPPING',
  PERFORMANCE_MAX = 'PERFORMANCE_MAX',
  DISCOVERY = 'DISCOVERY',
  SMART = 'SMART',
  DEMAND_GEN = 'DEMAND_GEN', // Google Demand Gen campaigns
}

export enum GoogleAdsCampaignStatus {
  ENABLED = 'ENABLED',
  PAUSED = 'PAUSED',
  REMOVED = 'REMOVED',
}

export enum GoogleAdsAdGroupType {
  SEARCH_STANDARD = 'SEARCH_STANDARD',
  DISPLAY_STANDARD = 'DISPLAY_STANDARD',
  VIDEO_RESPONSIVE = 'VIDEO_RESPONSIVE',
  VIDEO_SEQUENCE = 'VIDEO_SEQUENCE',
  PERFORMANCE_MAX = 'PERFORMANCE_MAX',
}

export interface GoogleAdsAccount {
  id: string; // Google Ads Customer ID (format: 123-456-7890)
  name: string;
  currencyCode: string;
  timeZone: string;
  isManagerAccount: boolean;
  isTestAccount: boolean;
  createdAt: Date;
  lastSyncedAt?: Date;
}

export interface GoogleAdsCampaign {
  id: string; // Google Ads Campaign ID
  accountId: string;
  name: string;
  type: GoogleAdsCampaignType;
  status: GoogleAdsCampaignStatus;
  
  // Budget
  budget?: {
    amount: number;
    currencyCode: string;
    deliveryMethod: 'STANDARD' | 'ACCELERATED';
    period?: 'DAILY' | 'FIXED_DAILY';
  };
  
  // Targeting
  targetAudience?: {
    demographics?: {
      ageRanges?: string[];
      genders?: string[];
      incomeRanges?: string[];
    };
    locations?: Array<{
      countryCode?: string;
      region?: string;
      city?: string;
    }>;
    languages?: string[];
    interests?: string[];
    keywords?: string[]; // For search campaigns
  };
  
  // Dates
  startDate?: Date;
  endDate?: Date;
  
  // Metrics
  metrics?: GoogleAdsCampaignMetrics;
  
  // Demand Gen specific
  isDemandGen?: boolean;
  demandGenSettings?: {
    audienceSignals?: string[];
    creativeAssets?: string[];
  };
  
  // YouTube specific
  videoCampaignSettings?: {
    videoIds?: string[];
    placementTargets?: string[];
  };
  
  createdAt: Date;
  lastUpdated: Date;
  lastSyncedAt?: Date;
}

export interface GoogleAdsCampaignMetrics {
  impressions: number;
  clicks: number;
  cost: number; // In account currency
  conversions: number;
  conversionValue: number;
  ctr: number; // Click-through rate
  cpc: number; // Cost per click
  cpm: number; // Cost per 1000 impressions
  cpa: number; // Cost per acquisition
  roas: number; // Return on ad spend
  videoViews?: number; // For video campaigns
  viewRate?: number; // For video campaigns
  averageCpv?: number; // Cost per view for video
}

export interface GoogleAdsAdGroup {
  id: string; // Google Ads Ad Group ID
  campaignId: string;
  name: string;
  type: GoogleAdsAdGroupType;
  status: GoogleAdsCampaignStatus;
  cpcBid?: number;
  targetCpa?: number;
  metrics?: GoogleAdsCampaignMetrics;
  createdAt: Date;
  lastUpdated: Date;
}

export interface GoogleAdsAd {
  id: string;
  adGroupId: string;
  campaignId: string;
  type: 'RESPONSIVE_SEARCH' | 'RESPONSIVE_DISPLAY' | 'VIDEO_RESPONSIVE' | 'VIDEO_SEQUENCE' | 'PERFORMANCE_MAX';
  status: GoogleAdsCampaignStatus;
  
  // Content
  headlines?: string[];
  descriptions?: string[];
  images?: string[];
  videos?: Array<{
    videoId: string;
    thumbnailUrl?: string;
  }>;
  
  // Performance
  metrics?: {
    impressions: number;
    clicks: number;
    ctr: number;
    conversions: number;
  };
  
  createdAt: Date;
  lastUpdated: Date;
}

export interface GoogleAdsKeyword {
  id: string;
  adGroupId: string;
  text: string;
  matchType: 'EXACT' | 'PHRASE' | 'BROAD';
  status: GoogleAdsCampaignStatus;
  cpcBid?: number;
  metrics?: {
    impressions: number;
    clicks: number;
    ctr: number;
    averageCpc: number;
    conversions: number;
    qualityScore?: number; // 1-10
  };
  createdAt: Date;
  lastUpdated: Date;
}

export interface GoogleAdsConversion {
  id: string;
  accountId: string;
  name: string;
  category: 'PURCHASE' | 'SIGNUP' | 'LEAD' | 'PAGE_VIEW' | 'DOWNLOAD' | 'OTHER';
  value?: number;
  count: number; // Number of conversions
  attributionModel: string;
  isPrimary: boolean;
  createdAt: Date;
}

export interface GoogleAdsReport {
  accountId: string;
  campaignId?: string;
  adGroupId?: string;
  period: {
    start: Date;
    end: Date;
  };
  metrics: GoogleAdsCampaignMetrics;
  dimensions?: Record<string, unknown>; // Additional breakdown data
  createdAt: Date;
}

export interface GoogleAdsIntegration {
  id: string;
  accountId: string; // Google Ads Customer ID
  accountName: string;
  refreshToken?: string; // Stored securely, used for API access
  accessToken?: string; // Temporary, expires
  tokenExpiresAt?: Date;
  isActive: boolean;
  lastSyncedAt?: Date;
  syncFrequency: 'hourly' | 'daily' | 'weekly';
  createdBy: string;
  createdAt: Date;
  lastUpdated: Date;
}

